import { Notification } from '@progress/kendo-react-notification';
import React from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { chevronUpIcon, chevronDownIcon } from '@progress/kendo-svg-icons';
import { useUiAdminConfigsContext, useGlobalizationContext, useTranslation } from '@cs-cms/state-management';
import { IdNamePair, CpsMessageBarType, CpsMessageBarState, CpsMaintenancePageState } from '@cs-cms/types';
import { StorageHelper } from '@cs-cms/utils';
import { RoutePath } from '../types/RoutePath';

type MessageStyleUnionType = 'warning' | 'error' | 'success' | 'info' | 'none';

const getKendoMessageType = (messageBarType: IdNamePair): { style: MessageStyleUnionType } => {
  switch (messageBarType.id) {
    case CpsMessageBarType.WARNING:
      return { style: 'warning' };
    case CpsMessageBarType.ERROR:
    case CpsMessageBarType.BLOCKED:
      return { style: 'error' };
    case CpsMessageBarType.SUCCESS:
      return { style: 'success' };
    case CpsMessageBarType.SEVERE_WARNING:
      return { style: 'warning' };
    default:
      return { style: 'info' };
  }
};

const generateMessageKey = (message: CpsMessageBarState): string => {
  return `messageBar_${message.messageContent?.slice(0, 50)}_${message.messageBarType}`;
};

export const CpsMessageBar: React.FC = () => {
  const { uiAdminConfigState, uiAdminConfigPreviewState } = useUiAdminConfigsContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);

  const [isMultiline, setIsMultiline] = React.useState(false);
  const [dismissedMessageKey, setDismissedMessageKey] = React.useState<string | null>(null);
  const [prevPreviewMessage, setPrevPreviewMessage] = React.useState<CpsMessageBarState | undefined>();

  if (uiAdminConfigPreviewState.messageBar !== prevPreviewMessage) {
    setDismissedMessageKey(null);
    setPrevPreviewMessage(uiAdminConfigPreviewState.messageBar as CpsMessageBarState | undefined);
  }

  const deriveMessageBar = (): CpsMessageBarState | undefined => {
    let message: CpsMessageBarState | undefined;

    if ((uiAdminConfigState.maintenancePage as CpsMaintenancePageState)?.enabled) {
      message = {
        enabled: true,
        messageContent:
          'ADMIN ONLY: Maintenance page is enabled for all users. To disable it go to Settings -> Admin Center -> Maintenance Page',
        messageBarType: {
          id: CpsMessageBarType.WARNING,
          name: translate('custom.settings.adminCenter.messageBar.type.warning')
        },
        truncated: false,
        hyperlinkText: 'PREVIEW',
        hyperlinkAddress: `${location.origin}/${RoutePath.MAINTENANCE}`
      };
    } else if (uiAdminConfigPreviewState.messageBar) {
      message = uiAdminConfigPreviewState.messageBar as CpsMessageBarState;
    } else if (uiAdminConfigState.messageBar) {
      message = uiAdminConfigState.messageBar as CpsMessageBarState;
    }

    if (!message) {
      return undefined;
    }

    const messageKey = generateMessageKey(message);
    if (dismissedMessageKey === messageKey) {
      return undefined;
    }
    if (message.isPreview) {
      return message;
    }
    if (StorageHelper.get(messageKey) === 'dismissed') {
      return undefined;
    }

    return message;
  };

  const messageBarToShow = deriveMessageBar();

  const handleDismiss = (message: CpsMessageBarState) => {
    const messageKey = generateMessageKey(message);

    setDismissedMessageKey(messageKey);

    if (!message.isPreview) {
      StorageHelper.set(messageKey, 'dismissed', 7);
    }
  };

  if (!messageBarToShow?.enabled) {
    return null;
  }

  return (
    <Notification
      key={generateMessageKey(messageBarToShow)}
      type={getKendoMessageType(messageBarToShow.messageBarType)}
      className="cpsMessageBar"
      style={{
        width: '100%'
      }}
      closable={true}
      onClose={() => handleDismiss(messageBarToShow)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div
          style={{
            maxHeight: !messageBarToShow.truncated ? 'none' : isMultiline ? 'none' : '20px',
            overflow: messageBarToShow.truncated ? 'hidden' : 'visible',
            whiteSpace: isMultiline ? 'pre-line' : 'normal',
            fontFamily: 'inherit',
            margin: 0
          }}
        >
          {messageBarToShow.messageContent}
        </div>

        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
          {messageBarToShow.hyperlinkAddress && (
            <div style={{ display: 'flex' }}>
              <Button
                themeColor="primary"
                fillMode={'outline'}
                onClick={() => window.open(messageBarToShow.hyperlinkAddress, '_blank')}
                style={{ height: '24px' }}
              >
                {messageBarToShow.hyperlinkText || ''}
              </Button>
            </div>
          )}
          <SvgIcon
            icon={isMultiline ? chevronUpIcon : chevronDownIcon}
            className="collapseIcon"
            onClick={() => setIsMultiline(!isMultiline)}
            style={{ cursor: 'pointer' }}
          />
        </div>
      </div>
    </Notification>
  );
};
